<template>
  <div class="w-100 bg-white border rounded-6">

    <!-- HEADER -->
    <div class="p-4 border-bottom-2" :class="{'text-center': isMobile}">
      <div class="row">
        <div :class="isMobile ? 'col-12 mb-3' : 'col-6'">
          <p class="body-2-2 font-bold mb-0" :class="isMobile ? 'text-16' : 'text-18 text-black'">{{ paymentName }}</p>
          <div class="w-100">
            <img v-if="tx_payment.use_guestpro_pg" src="../../assets/img/icon/payment/CC2.jpg" class="payment-logo">
            <img v-else src="../../assets/img/icon/payment/CC.jpg" class="payment-logo">
          </div>
        </div>
        <div :class="isMobile ? 'col-12' : 'col-6 text-right'">
          <p class="mb-0" :class="isMobile ? 'body-3-1' : 'body-2-1'">{{ $t('payment-result.payment-time') }}</p>
          <p class="mb-0 text-orange" :class="isMobile ? 'heading-6-1' : 'heading-5-1'">{{ countdownLabel }}</p>
        </div>
      </div>
    </div>

    <!-- FORM -->
    <div class="p-4">
      <div class="w-100 py-5 d-flex align-items-center justify-content-center" v-if="isSdkLoading">
        <loader />
      </div>

      <div v-show="!isSdkLoading">
        <div class="w-100 mb-4" ref="channel_container"></div>

        <div
          class="px-3 py-2 border radius-0 text-center d-block mb-3"
          style="background-color: rgb(255, 246, 243); border-color: rgb(254, 207, 184) !important;"
          v-if="errorMessage != ''">
          <p class="mb-0 body-2-1 mx-auto">{{ errorMessage }}</p>
        </div>

        <div class="d-flex align-items-stretch mb-3">
          <button
            class="ga-btn-primary-outline-lg action-cancel"
            :disabled="isSubmitting"
            :class="{'button-disabled': isSubmitting}"
            @click="cancelPayment">
            Cancel
          </button>
          <button
            class="ga-btn-primary-lg action-pay"
            :disabled="!isSubmitReady || isSubmitting || isTimeout"
            :class="{'button-disabled': !isSubmitReady || isSubmitting || isTimeout}"
            @click="submitPayment">
            <pulse-loader v-if="isSubmitting" color="#fff"></pulse-loader>
            <template v-else>Pay Now</template>
          </button>
        </div>

        <p class="mb-0 body-3-1 text-center secure-note">
          <i class="simple-icon-lock align-middle mr-1"></i>Secured by Xendit &middot; 3D Secure
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import config from '@/config/index.js';

  const SDK_CACHE_KEY = '__XENDIT_COMPONENTS_SDK__';
  const GENERIC_ERROR = 'We could not start the payment form. Please try again or choose another payment method.';
  const MOBILE_MAX_WIDTH = 768;

  const padTime = value => (value < 10 ? '0' + value : '' + value);

  export default {
    name: 'XenditComponentsCheckout',
    props: {
      tx_payment: {
        type: Object,
        default: () => ({})
      },
      channel_filter: {
        type: String,
        default: 'CARDS'
      }
    },
    data() {
      return {
        isSdkLoading: true,
        isSubmitReady: false,
        isSubmitting: false,
        isTimeout: false,
        errorMessage: '',
        countdown: moment.duration(0),
        windowWidth: window.innerWidth
      }
    },
    computed: {
      isMobile() {
        return this.windowWidth <= MOBILE_MAX_WIDTH
      },
      paymentName() {
        const method = this.tx_payment.payment_method
        return (method && method.name) || 'Credit Card'
      },
      expiredDate() {
        return moment.utc(this.tx_payment.created_at).local().add(this.tx_payment.duration, 'h').toDate()
      },
      isExpiredTrx() {
        return moment(moment().toDate()).isAfter(this.expiredDate)
      },
      countdownLabel() {
        if (this.isTimeout) return '00:00:00'
        return padTime(this.countdown.hours()) + ':' + padTime(this.countdown.minutes()) + ':' + padTime(this.countdown.seconds())
      }
    },
    created() {
      // Kept off `data` on purpose — the SDK instance, its DOM node and the timer must stay non-reactive.
      this.sdk = null
      this.listeners = null
      this.channelElement = null
      this.countdownTimer = null
    },
    mounted() {
      window.addEventListener('resize', this.handleResizeWindow)

      if (this.isExpiredTrx) {
        this.isTimeout = true
        this.$emit('expired')
        return
      }

      if (!this.tx_payment.components_sdk_key) {
        this.failWith('Payment session is unavailable. Please try again.')
        return
      }

      this.startCountdown()
      this.loadSdk()
        .then(SdkConstructor => this.setupSdk(SdkConstructor))
        .catch(() => this.failWith('Unable to load the payment form. Please try again.'))
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResizeWindow)
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.teardownSdk()
    },
    methods: {
      handleResizeWindow() {
        this.windowWidth = window.innerWidth
      },
      startCountdown() {
        const now = moment(moment().toDate()).format('YYYY-MM-DD HH:mm:ss')
        const expired = moment(this.expiredDate).format('YYYY-MM-DD HH:mm:ss')
        this.countdown = moment.duration(moment(expired).diff(moment(now)))

        this.countdownTimer = setInterval(() => {
          this.countdown = this.countdown.subtract(1, 'seconds')
          if (this.countdown.hours() <= 0 && this.countdown.minutes() <= 0 && this.countdown.seconds() <= 0) {
            clearInterval(this.countdownTimer)
            this.countdownTimer = null
            this.isTimeout = true
            this.$emit('expired')
          }
        }, 1000)
      },
      // The components UMD bundle claims window.Xendit, the same global as the legacy
      // Xendit.js used by XenditInputCC — grab the constructor, then hand the global back.
      loadSdk() {
        return new Promise((resolve, reject) => {
          if (window[SDK_CACHE_KEY]) {
            resolve(window[SDK_CACHE_KEY])
            return
          }

          const legacyXendit = window.Xendit
          const scriptTag = document.createElement('script')
          scriptTag.src = config.XENDIT_COMPONENTS_URL
          scriptTag.onload = () => {
            const SdkConstructor = window.Xendit && window.Xendit.XenditComponents
            if (legacyXendit) window.Xendit = legacyXendit

            if (!SdkConstructor) {
              reject()
              return
            }
            window[SDK_CACHE_KEY] = SdkConstructor
            resolve(SdkConstructor)
          }
          scriptTag.onerror = () => reject()
          document.head.appendChild(scriptTag)
        })
      },
      setupSdk(SdkConstructor) {
        this.sdk = new SdkConstructor({ componentsSdkKey: this.tx_payment.components_sdk_key })

        this.listeners = {
          'init': () => this.renderPaymentChannel(),
          'submission-ready': () => { this.isSubmitReady = true },
          'submission-not-ready': () => { this.isSubmitReady = false },
          'submission-begin': () => {
            this.isSubmitting = true
            this.errorMessage = ''
          },
          'submission-end': event => this.handleSubmissionEnd(event),
          'session-complete': () => {
            this.isSubmitting = false
            this.$emit('complete')
          },
          'session-expired-or-canceled': () => {
            this.isSubmitting = false
            this.$emit('expired')
          },
          'fatal-error': event => this.failWith(GENERIC_ERROR, event.message)
        }

        Object.keys(this.listeners).forEach(name => {
          this.sdk.addEventListener(name, this.listeners[name])
        })
      },
      renderPaymentChannel() {
        const channels = this.sdk.getActiveChannels({ filter: this.channel_filter })
        const channel = channels.length > 0 ? channels[0] : null

        if (!channel) {
          this.failWith('This payment method is not available right now. Please choose another one.')
          return
        }

        this.sdk.setCurrentChannel(channel)
        this.channelElement = this.sdk.createChannelComponent(channel, true)

        const container = this.$refs.channel_container
        while (container.firstChild) {
          container.removeChild(container.firstChild)
        }
        container.appendChild(this.channelElement)
        this.isSdkLoading = false
      },
      handleSubmissionEnd(event) {
        this.isSubmitting = false
        // Success is signalled by `session-complete`; only surface a message when Xendit gives one.
        this.errorMessage = event.userErrorMessage || ''
      },
      submitPayment() {
        if (!this.isSubmitReady) {
          this.sdk.showValidationErrors()
          return
        }
        this.sdk.submit()
      },
      cancelPayment() {
        this.$emit('cancel')
      },
      // `detail` carries the raw SDK message (often a stack trace) — console only, never the UI.
      failWith(message, detail) {
        if (detail) console.error('[XenditComponents]', detail)
        this.isSdkLoading = false
        this.isSubmitting = false
        this.errorMessage = message
        this.$emit('error', message)
      },
      teardownSdk() {
        if (!this.sdk) return

        if (this.listeners) {
          Object.keys(this.listeners).forEach(name => {
            this.sdk.removeEventListener(name, this.listeners[name])
          })
        }
        if (this.channelElement) this.sdk.destroyComponent(this.channelElement)

        this.sdk = null
        this.listeners = null
        this.channelElement = null
      }
    }
  }
</script>

<style scoped>
  .payment-logo{
    height: 38px;
    max-width: 100%;
  }
  /* Cancel keeps a fixed share so both buttons stay on one line down to the smallest screens. */
  .action-cancel{
    flex: 0 0 38%;
    margin-right: 12px;
  }
  .action-pay{
    flex: 1 1 auto;
  }
  .secure-note{
    color: #6c757d;
  }
</style>

<!-- Not scoped: the 3DS dialog is appended to <body> by the Xendit bundle, outside this component's tree. -->
<style>
  /* Xendit ships .xendit-dialog-backdrop as position:fixed with no z-index, so it loses to
     the concierge navbar (1041) and any Bootstrap sticky-top (1020). 10000 clears the app's 9999 ceiling. */
  .xendit-dialog-backdrop {
    z-index: 10000;
  }
</style>



// WEBPACK FOOTER //
// src/components/v2/XenditComponentsCheckout.vue